import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ClipboardCheck, ClipboardList, Gavel, MessageSquareMore, NotebookPen, Scale } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';

const featureCards = [
  {
    icon: Gavel,
    title: 'Live Voir Dire Workflow',
    description: 'Move from workspace to panel instantly and keep juror status decisions readable under pressure.',
  },
  {
    icon: Scale,
    title: 'Preserve Challenges',
    description: 'Track strike-for-cause issues, challenge posture, and peremptory thinking on the juror they belong to.',
  },
  {
    icon: NotebookPen,
    title: 'Juror-by-Juror Notes',
    description: 'Capture live notes, follow-up points, and notable traits without losing the juror-level context.',
  },
  {
    icon: MessageSquareMore,
    title: 'Team Chat',
    description: 'Keep presenter and support aligned with fast in-panel team chat during live voir dire.',
  },
  {
    icon: ClipboardCheck,
    title: 'Shared Checklists',
    description: 'Work through voir dire objectives with shared checklist items and visible team progress.',
  },
  {
    icon: ClipboardList,
    title: 'Trial-Ready Structure',
    description: 'Saved panels, session-based collaboration, and a professional workspace built for courtroom use.',
  },
];

export const AuthShell = ({
  badgeLabel,
  title,
  subtitle,
  error,
  message,
  children,
  footer,
  panelKey = 'default',
  panelPhase = 'idle',
  panelDirection = 'forward',
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [motionReady, setMotionReady] = useState(false);
  const [panelHeight, setPanelHeight] = useState(null);
  const panelBodyRef = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion) {
      setMotionReady(true);
      return undefined;
    }

    const schedule =
      typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame.bind(window)
        : (callback) => window.setTimeout(callback, 0);
    const cancel =
      typeof window.cancelAnimationFrame === 'function'
        ? window.cancelAnimationFrame.bind(window)
        : window.clearTimeout.bind(window);

    const handle = schedule(() => {
      setMotionReady(true);
    });

    return () => cancel(handle);
  }, [prefersReducedMotion]);

  useLayoutEffect(() => {
    if (prefersReducedMotion) {
      setPanelHeight(null);
      return undefined;
    }

    const node = panelBodyRef.current;
    if (!node) {
      return undefined;
    }

    const measure = () => {
      setPanelHeight(node.offsetHeight);
    };

    measure();

    let observer;
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver(measure);
      observer.observe(node);
    }

    return () => {
      observer?.disconnect();
    };
  }, [badgeLabel, children, error, footer, message, panelKey, prefersReducedMotion, subtitle, title]);

  return (
    <div
      className="app-shell auth-shell min-h-screen px-6 py-10"
      data-motion-state={motionReady ? 'open' : 'closed'}
      data-reduced-motion={prefersReducedMotion ? 'true' : 'false'}
      data-testid="auth-shell"
    >
      <div
        className="auth-shell__layout mx-auto grid max-w-6xl gap-8 lg:grid-cols-[minmax(0,1.1fr)_460px] lg:grid-rows-[auto_auto] lg:items-start lg:gap-x-8 lg:gap-y-10"
        data-testid="auth-shell-layout"
      >
        <section
          className="auth-shell__hero hidden lg:contents"
          data-testid="auth-shell-hero"
        >
          <div className="auth-shell__hero-copy lg:col-start-1 lg:row-start-1">
            <Badge variant="surface" className="w-fit">
              JuryTracker Pro
            </Badge>
            <h1 className="mt-6 max-w-xl text-5xl font-semibold tracking-tight text-slate-900">
              Jury selection built for fast courtroom collaboration.
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600">
              Sign in to manage panels, coordinate your team, and move from workspace to live voir dire without switching mental modes.
            </p>
          </div>

          <div className="auth-shell__features grid gap-4 md:grid-cols-3 lg:col-start-1 lg:row-start-2" data-testid="auth-shell-feature-grid">
            {featureCards.map(({ icon: Icon, title: cardTitle, description }, index) => (
              <div
                key={cardTitle}
                className="auth-shell__feature-card ds-surface border border-white/70 bg-white/85 p-5 backdrop-blur"
                data-testid="auth-shell-feature-card"
                style={{ '--auth-feature-index': index }}
              >
                <div className="inline-flex rounded-full bg-slate-100 p-2 text-slate-700">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="mt-4 text-sm font-semibold text-slate-900">{cardTitle}</div>
                <div className="mt-2 text-sm leading-6 text-slate-500">{description}</div>
              </div>
            ))}
          </div>
        </section>

        <section
          className="auth-shell__panel flex items-start justify-center lg:col-start-2 lg:row-start-2 lg:self-start"
          data-testid="auth-shell-panel"
        >
          <div className="auth-shell__panel-card w-full">
            <Card className="auth-shell__card w-full border border-indigo-100/70 bg-white/95 shadow-xl backdrop-blur">
              <div
                className="auth-shell__mode-shell"
                style={!prefersReducedMotion && panelHeight ? { height: `${panelHeight}px` } : undefined}
              >
                <div
                  key={panelKey}
                  ref={panelBodyRef}
                  className="auth-shell__mode-body"
                  data-direction={panelDirection}
                  data-phase={panelPhase}
                  data-testid="auth-shell-mode-body"
                >
                  <CardHeader className="pb-4">
                    {badgeLabel ? (
                      <Badge variant="surface" className="w-fit">
                        {badgeLabel}
                      </Badge>
                    ) : null}
                    <CardTitle className="mt-3 text-3xl text-slate-900">{title}</CardTitle>
                    {subtitle ? <p className="text-sm leading-6 text-slate-500">{subtitle}</p> : null}
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {error ? (
                      <div className="ds-surface border border-red-200 bg-red-50/80 p-4 text-sm text-red-700 shadow-none">
                        {error}
                      </div>
                    ) : null}
                    {message ? (
                      <div className="ds-surface border border-emerald-200 bg-emerald-50/80 p-4 text-sm text-emerald-700 shadow-none">
                        {message}
                      </div>
                    ) : null}
                    {children}
                    {footer ? <div className="pt-2">{footer}</div> : null}
                  </CardContent>
                </div>
              </div>
            </Card>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AuthShell;
